import React, { useState } from "react";
import styles from "./inventory-quantity-modal.module.css";
import { FbmListing } from "@/contexts/FbmListingContext";
import { submitFbmListings } from "@/api/fulfillment";
import { Product, ProductPatch, useInventoryContext } from "@/contexts/InventoryContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import Skeleton from "@/components/Skeleton/Skeleton";
import { productConditionToString } from "@/app/fba/utils";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import IconInfo from "@/components/icons/IconInfo";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Button from "@/components/forms/Button/Button";

type InventoryQuantityModalProps = {
  inventoryItem: Product;
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onClose: () => void;
};

//This component only focuses on editing the fbm listings qty.
const InventoryQuantityModal = ({
  inventoryItem,
  open,
  onOpenChange,
  onClose,
}: InventoryQuantityModalProps) => {
  const { suppliers, inventoryFlat, editProductInInventory } = useInventoryContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [quantity, setQuantity] = useState<number>(inventoryItem.sellerData[0].quantity);
  const [quantitySaveLoading, setQuantitySaveLoading] = useState<boolean>(false);
  const sellerData = inventoryItem.sellerData[0];

  const handleSaveQuantity = () => {
    setQuantitySaveLoading(true);

    const date = new Date();
    const listings: FbmListing[] = [
      {
        name: inventoryItem.name,
        asin: inventoryItem.asin,
        sku: sellerData.sku,
        image: inventoryItem.image,
        category: inventoryItem.category,
        priceUSD: sellerData.listPrice.toString(),
        quantity,
        shippingTemplateId: sellerData.shippingTemplateId,
        condition: sellerData.condition,
        supplier: suppliers.find(s => s.id === sellerData.supplierId)?.name,
        issues: [],
        errors: [],
        submittedAt: date.toISOString(),
        replenish: true, //This is only used to tell the backend endpoint to patch instead of put the listing.
      }
    ];

    submitFbmListings(listings)
      .then(listings => {
        //Loop through listings and modify records in inventory state.
        listings.map(listing => {
          const existingInventoryItem = inventoryFlat.find(i => i.sellerData[0].sku === listing.data.sku);

          //The inventory should ALWAYS exist at this point.
          if (!!existingInventoryItem) {
            const patch: ProductPatch = {
              status: "PROCESSING",
              sellerData: [
                {
                  ...existingInventoryItem.sellerData[0],
                  quantity,
                  status: "PROCESSING",
                }
              ]
            };

            editProductInInventory(listing.data.sku, patch);
          } else {
            console.error(`Inventory item for sku: ${listing.data.sku} not found`);
          }
        });
      })
      .catch(err => handleErrorResponseGenerically(err))
      .finally(() => {
        onClose();
        setQuantitySaveLoading(false);
      });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      onClose={onClose}
      className={styles.modal}
    >
      <ModalHeader>
        <div className={styles.header}>
          {sellerData.image ? (
            <img
              src={sellerData.image}
              width="64px"
              height="64px"
              alt={sellerData.name}
              className={styles.inventoryImage}
            />
          ): (
            <div className={styles.skeletonContainer}>
              <Skeleton type="image" disableShimmer width={64} height={64} />
            </div>
          )}
          <div className={styles.metaDataContainer}>
            <span className={styles.metaDataTitle}>
              {sellerData.name}
            </span>
            <div className={styles.metaDataSubContainer}>
              <span className={`${styles.subTitleContainer} ${styles.marginBottom}`}>
                <span className={styles.subMetaTitle}>
                  SKU:
                </span>
                <span className={styles.subMetaValue}>
                  {sellerData.sku}
                </span>
              </span>
              <span className={styles.subTitleContainer}>
                <span className={styles.subMetaTitle}>
                  ASIN:
                </span>
                <span className={styles.subMetaValue}>
                  {sellerData.asin}
                </span>
              </span>
              <div className={styles.skuAndCondition}>
                <span className={styles.subTitleContainer}>
                  <span className={styles.subMetaTitle}>
                    Condition:
                  </span>
                  <span className={styles.subMetaValue}>
                    {productConditionToString(sellerData.condition)}
                  </span>
                </span>
                <StatusBadge
                  status={sellerData.isFBM ? "danger" : "warning"}
                  label={sellerData.isFBM ? "FBM" : "FBA"}
                  className={styles.statusBadge}
                />
              </div>
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className={styles.modelBody}>
        <div className={styles.infoContainer}>
          <div className={styles.infoIcon}>
            <IconInfo />
          </div>
          <div className={styles.infoText}>Qty represents desired total in-stock quantity</div>
        </div>
        <InputNumber
          id="quantity"
          disabled={quantitySaveLoading}
          min={0}
          max={10000}
          value={quantity}
          onChange={setQuantity}
        />
      </ModalBody>
      <ModalFooter className={styles.modalFooter}>
        {quantitySaveLoading ? (
          <LoadingSpinner size="2rem" />
        ) : (
          <>
            <Button
              type={"secondary"}
              onClick={onClose}
            >
              Cancel
            </Button>
            {(quantity !== sellerData.quantity) && (
              <Button
                type={"primary"}
                onClick={handleSaveQuantity}
              >
                {(quantity > sellerData.quantity) && `Add (${quantity - sellerData.quantity}) items`}
                {(quantity < sellerData.quantity) && `Remove (${sellerData.quantity - quantity}) items`}
              </Button>
            )}
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default InventoryQuantityModal;
